import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Card } from '../../components/ui/card';
import api from '../../services/api';
import { toast } from 'sonner';
import AdminLayout from '../../components/layout/AdminLayout';
import { 
  FileText, Building, User, 
  Star, CheckCircle, XCircle
} from 'lucide-react';

const AdminRecordDetail = () => {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user?.is_admin) {
      navigate('/');
      return;
    }
    fetchRecord();
  }, [id, user, navigate]);

  const fetchRecord = async () => {
    try {
      const res = await api.get(`/admin/records/${id}`);
      setData(res.data);
    } catch (error) {
      toast.error('Failed to load record');
      navigate('/admin/records');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <AdminLayout title="Record Details" breadcrumbs={[{ label: 'Records', to: '/admin/records' }, { label: 'Details' }]}>
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1B5AFF]"></div>
        </div>
      </AdminLayout>
    );
  }

  const record = data?.record;
  const employee = data?.employee;
  const employer = data?.employer;

  const getRatingStars = (rating) => {
    return Array(5).fill(0).map((_, i) => (
      <Star key={i} className={`w-4 h-4 ${i < rating ? 'text-amber-400 fill-amber-400' : 'text-[#5B6472]'}`} />
    ));
  };

  return (
    <AdminLayout title="Record Details" breadcrumbs={[{ label: 'Records', to: '/admin/records' }, { label: 'Details' }]}>
      <div className="space-y-6">
        {/* Employee & Employer Info */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-6">
            <h3 className="text-lg font-bold text-[#0A0F1E] mb-4 flex items-center gap-2">
              <User className="w-5 h-5 text-[#1B5AFF]" />
              Employee
            </h3>
            <div className="space-y-2">
              <p className="text-[#0A0F1E]">
                {employee?.first_name} {employee?.last_name}
              </p>
              <p className="text-[#5B6472]">{employee?.email}</p>
              <p className="text-[#5B6472]">VETT ID: {employee?.vett_id || 'N/A'}</p>
              <p className="text-[#5B6472]">Job Title: {employee?.job_title || 'N/A'}</p>
            </div>
          </Card>

          <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-6">
            <h3 className="text-lg font-bold text-[#0A0F1E] mb-4 flex items-center gap-2">
              <Building className="w-5 h-5 text-blue-400" />
              Employer
            </h3>
            <div className="space-y-2">
              <p className="text-[#0A0F1E]">{employer?.company_name || 'Unknown'}</p>
              <p className="text-[#5B6472]">{employer?.email}</p>
              <p className="text-[#5B6472]">Industry: {employer?.industry || 'N/A'}</p>
            </div>
          </Card>
        </div>

        {/* Record Details */}
        <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-6 mb-8">
          <h3 className="text-lg font-bold text-[#0A0F1E] mb-4 flex items-center gap-2">
            <FileText className="w-5 h-5 text-[#1B5AFF]" />
            Record Information
          </h3>
          
          <div className="grid grid-cols-2 gap-4 mb-6">
            <div>
              <p className="text-[#5B6472] text-sm">Status</p>
              <p className="text-[#0A0F1E] capitalize">{record?.status?.replace('_', ' ')}</p>
            </div>
            <div>
              <p className="text-[#5B6472] text-sm">Job Title</p>
              <p className="text-[#0A0F1E]">{record?.job_title || 'N/A'}</p>
            </div>
            <div>
              <p className="text-[#5B6472] text-sm">Department</p>
              <p className="text-[#0A0F1E]">{record?.department || 'N/A'}</p>
            </div>
            <div>
              <p className="text-[#5B6472] text-sm">Employment Type</p>
              <p className="text-[#0A0F1E]">{record?.employment_type || 'N/A'}</p>
            </div>
            <div>
              <p className="text-[#5B6472] text-sm">Start Date</p>
              <p className="text-[#0A0F1E]">{record?.start_date || 'N/A'}</p>
            </div>
            <div>
              <p className="text-[#5B6472] text-sm">End Date</p>
              <p className="text-[#0A0F1E]">{record?.end_date || 'Current'}</p>
            </div>
          </div>

          {record?.rating && (
            <div className="mb-6">
              <p className="text-[#5B6472] text-sm mb-2">Overall Rating</p>
              <div className="flex gap-1">{getRatingStars(record.rating)}</div>
            </div>
          )}

          {/* Work Ethic Ratings */}
          {(record?.punctuality_rating || record?.deadline_rating || record?.initiative_rating || record?.adaptability_rating) && (
            <div className="mb-6">
              <p className="text-[#5B6472] text-sm mb-3">Work Ethic Ratings</p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {record.punctuality_rating && (
                  <div className="bg-[#F5F8FF]/30 p-3 rounded-lg">
                    <p className="text-[#5B6472] text-xs">Punctuality</p>
                    <div className="flex gap-1 mt-1">{getRatingStars(record.punctuality_rating)}</div>
                  </div>
                )}
                {record.deadline_rating && (
                  <div className="bg-[#F5F8FF]/30 p-3 rounded-lg">
                    <p className="text-[#5B6472] text-xs">Deadlines</p>
                    <div className="flex gap-1 mt-1">{getRatingStars(record.deadline_rating)}</div>
                  </div>
                )}
                {record.initiative_rating && (
                  <div className="bg-[#F5F8FF]/30 p-3 rounded-lg">
                    <p className="text-[#5B6472] text-xs">Initiative</p>
                    <div className="flex gap-1 mt-1">{getRatingStars(record.initiative_rating)}</div>
                  </div>
                )}
                {record.adaptability_rating && (
                  <div className="bg-[#F5F8FF]/30 p-3 rounded-lg">
                    <p className="text-[#5B6472] text-xs">Adaptability</p>
                    <div className="flex gap-1 mt-1">{getRatingStars(record.adaptability_rating)}</div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Skills */}
          {record?.skills && record.skills.length > 0 && (
            <div className="mb-6">
              <p className="text-[#5B6472] text-sm mb-2">Skills</p>
              <div className="flex flex-wrap gap-2">
                {record.skills.map((skill, i) => (
                  <span key={i} className="px-3 py-1 bg-[#1B5AFF]/20 text-[#1B5AFF] rounded-full text-sm">
                    {skill}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Rehire Eligibility */}
          {record?.rehire_eligibility && (
            <div className="mb-6">
              <p className="text-[#5B6472] text-sm mb-2">Rehire Eligibility</p>
              <div className="flex items-center gap-2">
                {record.rehire_eligibility === 'yes_without_reservation' && (
                  <>
                    <CheckCircle className="w-5 h-5 text-green-400" />
                    <span className="text-green-400">Yes, without reservation</span>
                  </>
                )}
                {record.rehire_eligibility === 'yes_with_right_role' && (
                  <>
                    <CheckCircle className="w-5 h-5 text-amber-400" />
                    <span className="text-amber-400">Yes, with right role</span>
                  </>
                )}
                {record.rehire_eligibility === 'no' && (
                  <>
                    <XCircle className="w-5 h-5 text-red-400" />
                    <span className="text-red-400">Would not rehire</span>
                  </>
                )}
              </div>
              {record.rehire_note && (
                <p className="text-[#0A0F1E] mt-2 text-sm">{record.rehire_note}</p>
              )}
            </div>
          )}

          {/* Closing Statement */}
          {record?.closing_statement && (
            <div className="mb-6">
              <p className="text-[#5B6472] text-sm mb-2">Closing Statement</p>
              <p className="text-[#0A0F1E]">{record.closing_statement}</p>
            </div>
          )}

          {/* Timestamps */}
          <div className="pt-4 border-t border-[#E8EDF5] text-sm text-[#5B6472]">
            <p>Created: {record?.created_at ? new Date(record.created_at).toLocaleString() : 'N/A'}</p>
            {record?.acknowledged_at && (
              <p>Acknowledged: {new Date(record.acknowledged_at).toLocaleString()}</p>
            )}
            {record?.archived_at && (
              <p>Archived: {new Date(record.archived_at).toLocaleString()}</p>
            )}
          </div>
        </Card>

        {/* Reviews */}
        {data?.reviews && data.reviews.length > 0 && (
          <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-6">
            <h3 className="text-lg font-bold text-[#0A0F1E] mb-4">Performance Reviews ({data.reviews.length})</h3>
            <div className="space-y-4">
              {data.reviews.map((review) => (
                <div key={review.id} className="bg-[#F5F8FF]/30 p-4 rounded-lg">
                  <div className="flex justify-between items-start mb-2">
                    <span className="text-[#1B5AFF] font-medium capitalize">{review.review_type} Review</span>
                    <div className="flex gap-1">{getRatingStars(review.performance_rating)}</div>
                  </div>
                  {review.key_achievements && (
                    <p className="text-[#0A0F1E] text-sm">{review.key_achievements}</p>
                  )}
                  <p className="text-[#5B6472] text-xs mt-2">
                    {new Date(review.created_at).toLocaleDateString()}
                  </p>
                </div>
              ))}
            </div>
          </Card>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminRecordDetail;
